import type { ValidationMessages } from "./types";

type StringFieldValidator = (args: { value: string }) => string | undefined;
type BooleanFieldValidator = (args: { value: boolean }) => string | undefined;

/**
 * Generic required-string validator with a configurable max length.
 * Use for any plain text field (name, company, institution, etc.).
 */
export const requiredString = (messages: ValidationMessages, maxLength: number): StringFieldValidator => {
  return ({ value }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    if (value.length > maxLength) {
      return messages.maxLength(maxLength);
    }
    return undefined;
  };
};

/**
 * Optional-string validator -- only validates max length when a value is provided.
 */
export const optionalString = (messages: ValidationMessages, maxLength: number): StringFieldValidator => {
  return ({ value }) => {
    if (value && value.trim().length > 0 && value.length > maxLength) {
      return messages.maxLength(maxLength);
    }
    return undefined;
  };
};

/** Email: required + max 254 chars + basic format check. */
export const emailValidator = (messages: ValidationMessages): StringFieldValidator => {
  return ({ value }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    if (value.length > 254) {
      return messages.maxLength(254);
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return messages.email;
    }
    return undefined;
  };
};

/** URL: required + max 2048 chars + basic format check. */
export const urlValidator = (messages: ValidationMessages): StringFieldValidator => {
  return ({ value }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    if (value.length > 2048) {
      return messages.maxLength(2048);
    }
    if (!/^(https?:\/\/)?[\w.-]+\.\w+.*$/.test(value)) {
      return messages.url;
    }
    return undefined;
  };
};

/** Phone: required + max 50 chars + format (digits, spaces, parens, dashes, dots, leading +) + min 7 digits. */
export const phoneValidator = (messages: ValidationMessages): StringFieldValidator => {
  return ({ value }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    if (value.length > 50) {
      return messages.maxLength(50);
    }
    if (!/^\+?[\d\s().\-]+$/.test(value)) {
      return messages.phone;
    }
    const digitCount = value.replace(/\D/g, "").length;
    if (digitCount < 7) {
      return messages.phone;
    }
    return undefined;
  };
};

/** Country: required (non-empty string). */
export const countryValidator = (messages: ValidationMessages): StringFieldValidator => {
  return ({ value }) => {
    if (!value || value.trim().length === 0) {
      return messages.required;
    }
    return undefined;
  };
};

/** Terms / checkbox: must be true. */
export const termsAgreedValidator = (messages: ValidationMessages): BooleanFieldValidator => {
  return ({ value }) => {
    if (!value) {
      return messages.termsRequired;
    }
    return undefined;
  };
};
